import type { DocumentType, TripEventType } from '@kmdx/contracts';
import clsx from 'clsx';
import {
  Camera,
  Check,
  ChevronRight,
  Clock,
  FileSignature,
  Gauge,
  MapPin,
  Navigation,
  PackageCheck,
  PackageOpen,
  Phone,
  PhoneCall,
  PenLine,
  RotateCcw,
  Truck,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CaptureFlow } from '../components/capture-flow';
import { Banner, BigButton, Card, Label, LinkButton, Screen, TextArea } from '../components/ui';
import { recordEvent, saveOdometer, sendMessage } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { dayTime, telHref, timeOf } from '../lib/format';
import { navigationLinks } from '../lib/geo';
import { countryName, DOCUMENT_LABELS } from '../lib/labels';
import {
  deliveryDocNeeds,
  electronicOrders,
  pickupCmrDone,
  useMissingDocuments,
  useOdometerReading,
  useOpenTasks,
} from '../lib/requirements';
import { navigate } from '../lib/router';
import { nextStopOf, useActiveTrips, useStops } from '../lib/selectors';
import type { EventRecord, OdometerReading, PhotoRecord, ReshootTask, StopDto } from '../lib/types';
import type { TripRecord } from '../lib/db';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  „ДНЕС" — ЕКРАНЪТ-ПЪТЕВОДИТЕЛ.
 *
 *  Шофьорът трябва да вижда три неща, без да мисли:
 *    1. докъде е стигнал — „Стъпка 3 от 8" и маршрутът с всички адреси;
 *    2. какво следва — стъпките на текущия адрес, по ред, с отметки;
 *    3. какво да натисне сега — ЕДИН голям зелен бутон на текущата стъпка.
 *
 *  Поредицата е една и съща на всеки адрес:
 *    начало:        Километраж (снимка + число)
 *    товарене:      Тръгвам → Пристигнах → ЧМР (е-ЧМР или снимка) → Натоварих
 *    разтоварване:  Тръгвам → Пристигнах → Разтоварих → снимки → Подпис на получателя
 *    край:          Километраж → Приключи курса
 *
 *  Документ, който клиентът иска, е СТЪПКА — следващата не се отключва,
 *  докато шофьорът не го заснеме и не потвърди, че се чете. Върнат от офиса
 *  документ спира всичко, докато не се заснеме наново (вж. `requirements.ts`).
 * ═══════════════════════════════════════════════════════════════════════════
 */

type StepKey = 'odo' | 'go' | 'arrive' | 'cmr' | 'load' | 'unload' | 'sign' | `doc:${DocumentType}`;

interface Step {
  key: StepKey;
  title: string;
  hint: string;
  done: boolean;
  at: string | null;
  /** Какво пише до отметката, когато стъпката е минала по друг път (хартия вместо е-ЧМР). */
  doneNote?: string;
  /** За стъпките „Снимай …": кой документ. */
  docType?: DocumentType;
}

interface FlowContext {
  events: EventRecord[];
  photos: PhotoRecord[];
  /** Заявките с електронна ЧМР — при тях хартиена ЧМР на разтоварване не се снима. */
  electronic: Set<string>;
  /**
   * Километражът при тръгване: стъпка на първия адрес. „skip" — курсът е
   * тръгнал отпреди обновяването и началото вече не може да се заснеме вярно.
   */
  startOdometer: 'needed' | 'done' | 'skip';
  firstStopId: string | null;
}

function stepsFor(stop: StopDto, ctx: FlowContext): Step[] {
  const mine = ctx.events.filter((e) => e.orderStopId === stop.id);
  const at = (...types: TripEventType[]) =>
    mine
      .filter((e) => types.includes(e.type))
      .sort((a, b) => b.at.localeCompare(a.at))[0]?.at ?? null;
  const photoAt = (type: DocumentType) =>
    ctx.photos
      .filter((p) => p.orderStopId === stop.id && p.type === type)
      .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))[0]?.capturedAt ?? null;
  const started = stop.status !== 'pending';
  const arrived = stop.status === 'arrived' || stop.status === 'completed';
  const completed = stop.status === 'completed';

  const lead: Step[] =
    stop.id === ctx.firstStopId && ctx.startOdometer !== 'skip'
      ? [
          {
            key: 'odo',
            title: 'Километраж при тръгване',
            hint: 'Снимка на таблото и числото — от тях се смятат километрите за заплатата.',
            done: ctx.startOdometer === 'done' || started,
            at: null,
          },
        ]
      : [];

  if (stop.kind === 'pickup') {
    const cmr = pickupCmrDone(stop, ctx.photos, ctx.events);
    return [
      ...lead,
      { key: 'go', title: 'Тръгвам към товаренето', hint: 'Диспечерът вижда, че сте на път.', done: started, at: at('started', 'departed') },
      { key: 'arrive', title: 'Пристигнах', hint: 'Часът и мястото се записват сами.', done: arrived, at: at('arrived_pickup') },
      {
        key: 'cmr',
        title: 'ЧМР',
        hint: 'Подписи на телефона (е-ЧМР) или снимка на хартиената ЧМР — без нея бусът не тръгва.',
        done: cmr !== null || completed,
        doneNote: cmr === 'ecmr' ? 'подписана на телефона' : cmr === 'paper' ? 'хартиена, заснета' : 'на хартия',
        at: at('ecmr_signed') ?? photoAt('cmr'),
      },
      { key: 'load', title: 'Натоварих', hint: 'Товарът е в буса — клиентът започва да го вижда на картата.', done: completed, at: at('loaded') },
    ];
  }

  const needs = deliveryDocNeeds(stop, ctx.photos, ctx.electronic);
  return [
    ...lead,
    { key: 'go', title: 'Тръгвам към разтоварването', hint: 'Диспечерът и клиентът виждат, че товарът пътува.', done: started, at: at('started', 'departed') },
    { key: 'arrive', title: 'Пристигнах', hint: 'Часът и мястото се записват сами.', done: arrived, at: at('arrived_delivery') },
    { key: 'unload', title: 'Разтоварих', hint: 'Стоката е свалена от буса.', done: completed || mine.some((e) => e.type === 'unloaded'), at: at('unloaded') },
    ...needs.map(
      (need): Step => ({
        key: `doc:${need.type}`,
        title: need.type === 'cmr' ? 'Снимай подписаната ЧМР' : `Снимай: ${need.label}`,
        hint:
          need.type === 'cmr'
            ? 'С подписа и печата на получателя — всички листове.'
            : 'Целият лист, така че да се чете.',
        done: need.done || completed,
        docType: need.type,
        at: photoAt(need.type),
      }),
    ),
    { key: 'sign', title: 'Подпис на получателя', hint: 'Това е и подписът по ЧМР. Без него доставката не е завършена.', done: completed, at: at('pod_captured') },
  ];
}

function useTripEvents(tripId: string | null): EventRecord[] {
  return useLive(async () => (tripId ? db.events.where('tripId').equals(tripId).toArray() : []), [tripId], []);
}

function useTripPhotos(tripId: string | null): PhotoRecord[] {
  return useLive(async () => (tripId ? db.photos.where('tripId').equals(tripId).toArray() : []), [tripId], []);
}

export function TodayScreen() {
  const trips = useActiveTrips();
  const trip = trips[0] ?? null;
  const stops = useStops(trip?.id ?? null);
  const events = useTripEvents(trip?.id ?? null);
  const photos = useTripPhotos(trip?.id ?? null);
  const tasks = useOpenTasks();
  const startReading = useOdometerReading(trip?.id ?? null, 'start');
  const current = nextStopOf(stops);

  /**
   * Смени ли се адресът, екранът се връща най-горе. Иначе шофьорът натиска
   * „Натоварих" долу в списъка и остава там — новият адрес е над погледа му
   * и изглежда, че нищо не се е случило.
   */
  const lastStopId = useRef<string | null>(null);
  useEffect(() => {
    if (current?.id && lastStopId.current && current.id !== lastStopId.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    lastStopId.current = current?.id ?? null;
  }, [current?.id]);

  if (!trip) {
    return (
      <Screen title="Днес" logo>
        <ReshootTasks tasks={tasks} />
        <div className="mt-6 flex flex-col items-center gap-4 text-center">
          <div className="flex size-24 items-center justify-center rounded-full bg-accent-soft text-go">
            <Truck size={48} />
          </div>
          <h2 className="text-2xl font-bold">Няма курс в момента</h2>
          <p className="max-w-xs text-xl text-muted">
            Диспечерът ще ви изпрати следващия. Когато дойде, ще се появи тук с всички стъпки.
          </p>
        </div>
      </Screen>
    );
  }

  const accepting = trip.status === 'dispatched';
  const firstStop = stops[0] ?? null;
  const ctx: FlowContext = {
    events,
    photos,
    electronic: electronicOrders(stops, events),
    startOdometer:
      startReading || trip.startOdometerKm != null
        ? 'done'
        : firstStop?.status === 'pending'
          ? 'needed'
          : 'skip',
    firstStopId: firstStop?.id ?? null,
  };
  const allSteps = stops.flatMap((s) => stepsFor(s, ctx));
  const total = allSteps.length + 1;
  const done = (accepting ? 0 : 1) + allSteps.filter((s) => s.done).length;
  /** Върнат от офиса документ спира всяка следваща стъпка, докато не се заснеме наново. */
  const blocked = tasks.length > 0;

  return (
    <Screen title={`Курс ${trip.number}`} subtitle={trip.customerName} logo>
      <div className="space-y-4">
        <ReshootTasks tasks={tasks} />
        <Progress done={done} total={total} finished={!accepting && !current} />
        <RouteStrip stops={stops} current={accepting ? null : current} />

        {accepting ? (
          <AcceptTrip trip={trip} stops={stops} />
        ) : current ? (
          <StopFlow
            trip={trip}
            stop={current}
            steps={stepsFor(current, ctx)}
            index={stops.indexOf(current)}
            count={stops.length}
            blocked={blocked}
            startReading={current.id === firstStop?.id ? startReading : null}
          />
        ) : (
          <FinishTrip trip={trip} blocked={blocked} startReading={startReading} />
        )}

        {!accepting ? <ProblemButton trip={trip} stopId={current?.id ?? null} /> : null}

        {trips.length > 1 ? (
          <Card>
            <Label>Следващ курс</Label>
            <div className="mt-1 text-xl font-semibold">
              {trips[1]?.number} · {dayTime(trips[1]?.plannedStartAt ?? null)}
            </div>
          </Card>
        ) : null}
      </div>
    </Screen>
  );
}

/** ─── Напредък и маршрут ─────────────────────────────────────────────── */

function Progress({ done, total, finished }: { done: number; total: number; finished: boolean }) {
  const pct = Math.round((Math.min(done, total) / total) * 100);
  return (
    <div>
      <div className="flex items-baseline justify-between text-lg">
        <span className="font-semibold">{finished ? 'Всички стъпки са минати' : `Стъпка ${Math.min(done + 1, total)} от ${total}`}</span>
        <span className="text-muted">{pct}%</span>
      </div>
      <div className="mt-2 h-3 overflow-hidden rounded-full bg-card">
        <div className="h-full rounded-full bg-go transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function RouteStrip({ stops, current }: { stops: StopDto[]; current: StopDto | null }) {
  if (stops.length === 0) return null;
  return (
    <ol className="-mx-3 flex gap-2 overflow-x-auto px-3 pb-1" aria-label="Маршрут">
      {stops.map((stop, i) => {
        const isCurrent = current?.id === stop.id;
        const isDone = stop.status === 'completed';
        return (
          <li key={stop.id} className="flex shrink-0 items-center gap-2">
            {i > 0 ? <ChevronRight size={22} className="text-muted" aria-hidden="true" /> : null}
            <div
              className={clsx(
                'flex items-center gap-2 rounded-2xl border-2 px-3 py-2',
                isCurrent ? 'border-accent bg-accent-soft' : isDone ? 'border-go/40 bg-card' : 'border-line bg-card',
              )}
            >
              <span
                className={clsx(
                  'flex size-8 shrink-0 items-center justify-center rounded-full text-base font-bold',
                  isDone ? 'bg-go text-white' : isCurrent ? 'bg-brand text-accent' : 'bg-paper text-muted',
                )}
              >
                {isDone ? <Check size={18} strokeWidth={3} /> : i + 1}
              </span>
              <span className="leading-tight">
                <span className="block text-sm font-semibold uppercase tracking-wide text-muted">
                  {stop.kind === 'pickup' ? 'Товарене' : 'Разтоварване'}
                </span>
                <span className="block text-lg font-semibold">{stop.city}</span>
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** ─── Приемане на курса ───────────────────────────────────────────────── */

function AcceptTrip({ trip, stops }: { trip: TripRecord; stops: StopDto[] }) {
  const [busy, setBusy] = useState(false);
  const first = stops[0];
  return (
    <div className="space-y-4">
      <Card className="!bg-accent-soft !border-accent">
        <div className="text-lg font-semibold">Нов курс за вас</div>
        <p className="mt-1 text-xl">
          Прегледайте адресите и натиснете <b>„Приемам курса“</b>. След това стъпките ще ви водят една по една.
        </p>
      </Card>

      <Card>
        <Label>Адреси по ред</Label>
        <ol className="mt-2 space-y-3">
          {stops.map((stop, i) => (
            <li key={stop.id} className="flex gap-3">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand text-base font-bold text-accent">{i + 1}</span>
              <span className="min-w-0">
                <span className="block text-base font-semibold uppercase tracking-wide text-muted">
                  {stop.kind === 'pickup' ? 'Товарене' : 'Разтоварване'} · {stop.windowFrom ? dayTime(stop.windowFrom) : 'без час'}
                </span>
                <span className="block text-xl font-semibold">{stop.name}</span>
                <span className="block text-lg text-muted">
                  {stop.city}, {countryName(stop.country)}
                </span>
              </span>
            </li>
          ))}
        </ol>
        {first?.goodsDescription || first?.weightKg ? (
          <p className="mt-3 border-t-2 border-line pt-3 text-lg">
            <span className="text-muted">Товар: </span>
            {first?.goodsDescription ?? '—'}
            {first?.weightKg ? ` · ${first.weightKg} кг` : ''}
          </p>
        ) : null}
        {trip.vehicle ? <p className="mt-1 text-lg text-muted">Бус {trip.vehicle.plate}</p> : null}
      </Card>

      <BigButton
        tone="accent"
        disabled={busy}
        icon={<Check size={28} strokeWidth={3} />}
        onClick={() => {
          setBusy(true);
          void recordEvent({ tripId: trip.id, type: 'accepted' }).then(() =>
            db.trips.update(trip.id, { status: 'accepted' }).finally(() => setBusy(false)),
          );
        }}
      >
        Приемам курса
      </BigButton>
      <BigButton
        tone="plain"
        disabled={busy}
        onClick={() => {
          const reason = window.prompt('Защо не можете да поемете курса?');
          if (reason === null) return;
          setBusy(true);
          /* Отказаният курс излиза от „текущите" веднага — следващият не бива да чака изтегляне. */
          void recordEvent({ tripId: trip.id, type: 'rejected', note: reason.slice(0, 1000) })
            .then(() => db.trips.update(trip.id, { status: 'planned' }))
            .finally(() => setBusy(false));
        }}
      >
        Не мога да го поема
      </BigButton>
    </div>
  );
}

/** ─── Текущият адрес и стъпките му ───────────────────────────────────── */

function StopFlow({
  trip,
  stop,
  steps,
  index,
  count,
  blocked,
  startReading,
}: {
  trip: TripRecord;
  stop: StopDto;
  steps: Step[];
  index: number;
  count: number;
  blocked: boolean;
  /** Километражът при тръгване — тръгва с първото „Тръгвам" на курса. */
  startReading: OdometerReading | null;
}) {
  const isPickup = stop.kind === 'pickup';
  const links = navigationLinks({ lat: stop.lat, lon: stop.lon, address: stop.address, city: stop.city, country: stop.country });
  const dispatcherTel = telHref(trip.dispatcher?.phone ?? null);
  const siteTel = telHref(stop.contactPhone);
  const photoCount = useLive(() => db.photos.where('orderStopId').equals(stop.id).count(), [stop.id], 0);
  const currentIndex = steps.findIndex((s) => !s.done);
  /**
   * Снимането живее тук, не в стъпката: първата потвърдена страница вече
   * отмята стъпката и тя изчезва — заедно с камерата, ако беше нейна. ЧМР има
   * няколко листа, затова екранът „Още страница / Готово" трябва да оцелее.
   */
  const [capturing, setCapturing] = useState<DocumentType | null>(null);

  return (
    <div className="space-y-4">
      <Card className="!bg-paper">
        <div className="flex items-center justify-between gap-2">
          <span
            className={clsx(
              'inline-flex items-center gap-2 rounded-full px-3 py-1 text-base font-bold uppercase tracking-wide',
              isPickup ? 'bg-brand text-accent' : 'bg-go text-white',
            )}
          >
            {isPickup ? <PackageCheck size={18} /> : <PackageOpen size={18} />}
            {isPickup ? 'Товарене' : 'Разтоварване'}
          </span>
          <span className="text-base text-muted">
            адрес {index + 1} от {count}
          </span>
        </div>
        <h2 className="mt-3 text-3xl font-bold leading-tight">{stop.name}</h2>
        <p className="mt-1 flex items-start gap-2 text-xl">
          <MapPin size={22} className="mt-1 shrink-0 text-muted" />
          <span>
            {stop.address}, {stop.postcode ? `${stop.postcode} ` : ''}
            {stop.city}, {countryName(stop.country)}
          </span>
        </p>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xl">
          <Clock size={22} className="shrink-0 text-muted" />
          <span className="font-semibold">{stop.windowFrom ? dayTime(stop.windowFrom) : 'без определен час'}</span>
          {stop.windowTo ? <span>до {timeOf(stop.windowTo)}</span> : null}
          {stop.isHardDeadline ? (
            <span className="rounded-lg bg-stop px-2 py-0.5 text-base font-bold text-white">краен срок</span>
          ) : null}
        </p>
        {stop.reference ? (
          <p className="mt-2 text-xl">
            <span className="text-muted">Референция: </span>
            <span className="font-semibold">{stop.reference}</span>
          </p>
        ) : null}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <LinkButton href={links.google} icon={<Navigation size={24} />}>
            Навигация
          </LinkButton>
          {siteTel ? (
            <LinkButton href={siteTel} icon={<Phone size={24} />}>
              Обекта
            </LinkButton>
          ) : dispatcherTel ? (
            <LinkButton href={dispatcherTel} icon={<PhoneCall size={24} />}>
              Диспечер
            </LinkButton>
          ) : (
            <span />
          )}
        </div>
      </Card>

      {stop.instructions ? <Banner tone="warn">{stop.instructions}</Banner> : null}

      <section aria-label="Стъпки на този адрес">
        <Label>Какво следва на този адрес</Label>
        <ol className="mt-2">
          {steps.map((step, i) => (
            <StepRow
              key={step.key}
              step={step}
              n={i + 1}
              state={step.done ? 'done' : i === currentIndex ? 'current' : 'next'}
              last={i === steps.length - 1}
            >
              {i === currentIndex ? (
                <StepAction
                  trip={trip}
                  stop={stop}
                  step={step}
                  blocked={blocked}
                  startReading={startReading}
                  onCapture={setCapturing}
                />
              ) : null}
            </StepRow>
          ))}
        </ol>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <BigButton tone="plain" icon={<Camera size={26} />} onClick={() => navigate(`/stop/${stop.id}/docs`)}>
          Снимки{photoCount > 0 ? <span className="text-base"> ({photoCount})</span> : null}
        </BigButton>
        <BigButton tone="plain" onClick={() => navigate(`/stop/${stop.id}`)}>
          Подробно
        </BigButton>
      </div>

      {dispatcherTel && siteTel ? (
        <LinkButton href={dispatcherTel} icon={<PhoneCall size={24} />}>
          Обади се на диспечера
        </LinkButton>
      ) : null}

      {capturing ? (
        <CaptureFlow
          type={capturing}
          title={capturing === 'cmr' && !isPickup ? 'Подписаната ЧМР' : undefined}
          tripId={trip.id}
          orderStopId={capturing === 'odometer' ? null : stop.id}
          askOdometer={capturing === 'odometer'}
          onClose={() => setCapturing(null)}
          onDone={(result) => {
            const photo = result.photos[0];
            if (capturing === 'odometer' && photo && result.odometerKm !== null) {
              void saveOdometer(trip.id, 'start', { km: result.odometerKm, docId: photo.id });
            }
            setCapturing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function StepRow({
  step,
  n,
  state,
  last,
  children,
}: {
  step: Step;
  n: number;
  state: 'done' | 'current' | 'next';
  last: boolean;
  children?: ReactNode;
}) {
  return (
    <li className="relative flex gap-3 pb-4">
      {!last ? (
        <span
          className={clsx('absolute left-[19px] top-11 bottom-0 w-1 rounded-full', state === 'done' ? 'bg-go' : 'bg-line')}
          aria-hidden="true"
        />
      ) : null}
      <span
        className={clsx(
          'relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full border-2 text-lg font-bold',
          state === 'done' && 'border-go bg-go text-white',
          state === 'current' && 'border-brand bg-accent text-accent-ink ring-4 ring-accent/40',
          state === 'next' && 'border-line bg-paper text-muted',
        )}
      >
        {state === 'done' ? <Check size={22} strokeWidth={3} /> : n}
      </span>
      <div
        className={clsx(
          'min-w-0 flex-1',
          state === 'current' && 'rounded-2xl border-2 border-accent bg-accent-soft p-3',
        )}
      >
        <div className={clsx('text-xl font-bold leading-tight', state === 'next' && 'text-muted')}>{step.title}</div>
        <div className={clsx('mt-0.5 text-base', state === 'done' ? 'text-go' : 'text-muted')}>
          {state === 'done'
            ? [step.doneNote, step.at ? `готово в ${timeOf(step.at)}` : step.doneNote ? null : 'готово']
                .filter(Boolean)
                .join(' · ')
            : step.hint}
        </div>
        {children ? <div className="mt-3 space-y-2">{children}</div> : null}
      </div>
    </li>
  );
}

function StepAction({
  trip,
  stop,
  step,
  blocked,
  startReading,
  onCapture,
}: {
  trip: TripRecord;
  stop: StopDto;
  step: Step;
  blocked: boolean;
  startReading: OdometerReading | null;
  /** Отваря камерата за документ — самата камера е в `StopFlow`. */
  onCapture: (type: DocumentType) => void;
}) {
  const [busy, setBusy] = useState(false);
  const isPickup = stop.kind === 'pickup';

  if (blocked) {
    return <Banner tone="stop">Първо заснемете наново върнатия документ — задачата е най-горе.</Banner>;
  }

  async function fire(type: TripEventType, payload?: Record<string, unknown>) {
    setBusy(true);
    try {
      await recordEvent({ tripId: trip.id, orderStopId: stop.id, type, payload: payload ?? null });
      if (trip.status !== 'in_progress') await db.trips.update(trip.id, { status: 'in_progress' });
    } finally {
      setBusy(false);
    }
  }

  /** Снимане по стъпка: камера → „Ясно ли е?" → потвърдено. Стъпката се отмята сама. */
  if (step.key.startsWith('doc:') && step.docType) {
    const type = step.docType;
    return (
      <BigButton tone="accent" icon={<Camera size={28} />} onClick={() => onCapture(type)}>
        {type === 'cmr' ? 'Снимай ЧМР' : 'Снимай'}
      </BigButton>
    );
  }

  switch (step.key) {
    case 'odo':
      return (
        <BigButton tone="accent" icon={<Gauge size={28} />} onClick={() => onCapture('odometer')}>
          Снимай километража
        </BigButton>
      );
    case 'go': {
      /** Първото тръгване по курса носи километража от таблото. */
      const payload = startReading ? { odometerKm: startReading.km, odometerDocId: startReading.docId } : undefined;
      return (
        <BigButton
          tone="accent"
          disabled={busy}
          icon={<Truck size={28} />}
          onClick={() => void fire(trip.status === 'in_progress' ? 'departed' : 'started', payload)}
        >
          Тръгвам натам
        </BigButton>
      );
    }
    case 'arrive':
      return (
        <BigButton tone="accent" disabled={busy} icon={<MapPin size={28} />} onClick={() => void fire(isPickup ? 'arrived_pickup' : 'arrived_delivery')}>
          Пристигнах
        </BigButton>
      );
    case 'cmr':
      return (
        <>
          <BigButton tone="accent" icon={<PenLine size={28} />} onClick={() => navigate(`/stop/${stop.id}/ecmr`)}>
            Подписи по е-ЧМР
          </BigButton>
          <BigButton tone="plain" icon={<Camera size={26} />} onClick={() => onCapture('cmr')}>
            Хартиена ЧМР — снимай я
          </BigButton>
        </>
      );
    case 'load':
      return (
        <BigButton tone="accent" disabled={busy} icon={<PackageCheck size={28} />} onClick={() => void fire('loaded')}>
          Натоварих
        </BigButton>
      );
    case 'unload':
      return (
        <BigButton tone="accent" disabled={busy} icon={<PackageOpen size={28} />} onClick={() => void fire('unloaded')}>
          Разтоварих
        </BigButton>
      );
    case 'sign':
      return (
        <BigButton tone="accent" icon={<FileSignature size={28} />} onClick={() => navigate(`/stop/${stop.id}/sign`)}>
          Подпис на получателя
        </BigButton>
      );
  }
}

/** ─── Проблем по пътя ─────────────────────────────────────────────────── */

/**
 * „Закъснявам" е събитие по курса и бележката му се вижда в портала на
 * клиента — затова шофьорът го чете, преди да пише. Отива и като съобщение
 * до диспечера, иначе остава незабелязано в хронологията.
 * „Имам проблем" не пише нищо към клиента: повреда или катастрофа се
 * решават по телефона с диспечера, а той преценява какво да каже навън.
 */
function ProblemButton({ trip, stopId }: { trip: TripRecord; stopId: string | null }) {
  const [open, setOpen] = useState<null | 'delayed' | 'problem'>(null);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const dispatcherTel = telHref(trip.dispatcher?.phone ?? null);

  if (sent) return <Banner tone="go">Изпратено. Диспечерът и клиентът виждат закъснението.</Banner>;

  if (!open) {
    return (
      /* Без икони: с икона „Закъснявам" не се побира в половин екран на 390 px. */
      <div className="grid grid-cols-2 gap-3">
        <BigButton tone="plain" onClick={() => setOpen('delayed')}>
          Закъснявам
        </BigButton>
        <BigButton tone="plain" onClick={() => setOpen('problem')}>
          Имам проблем
        </BigButton>
      </div>
    );
  }

  if (open === 'problem') {
    return (
      <Card>
        <div className="text-xl font-bold">Проблем по пътя</div>
        <p className="mt-1 text-lg text-muted">Повреда, катастрофа, проблем с товара или на рампата — кажете на диспечера.</p>
        <div className="mt-3 space-y-3">
          {dispatcherTel ? (
            <LinkButton href={dispatcherTel} tone="stop" icon={<PhoneCall size={24} />}>
              Обади се на диспечера
            </LinkButton>
          ) : null}
          <BigButton tone="plain" onClick={() => navigate('/messages')}>
            Пиши на диспечера
          </BigButton>
          <button type="button" onClick={() => setOpen(null)} className="tap w-full py-2 text-lg text-muted underline">
            Затвори
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="text-xl font-bold">Закъснявам</div>
      <p className="mt-1 text-lg text-muted">С колко и защо?</p>
      <div className="mt-3">
        <TextArea label="Например: около 1 час, задръстване на границата" value={note} onChange={setNote} rows={3} />
      </div>
      <p className="mt-2 text-base text-muted">Това се вижда от диспечера и от клиента. Пишете само за пътя и товара.</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <BigButton tone="plain" onClick={() => setOpen(null)}>
          Отказ
        </BigButton>
        <BigButton
          tone="accent"
          disabled={busy || note.trim().length < 3}
          onClick={() => {
            const text = note.trim();
            setBusy(true);
            void recordEvent({ tripId: trip.id, orderStopId: stopId, type: 'delayed', note: text })
              .then(() => sendMessage(`Закъснявам: ${text}`, trip.id))
              .then(() => {
                setSent(true);
                setNote('');
                setOpen(null);
                window.setTimeout(() => setSent(false), 6000);
              })
              .finally(() => setBusy(false));
          }}
        >
          Изпрати
        </BigButton>
      </div>
    </Card>
  );
}

/** ─── Край на курса ───────────────────────────────────────────────────── */

function FinishTrip({
  trip,
  blocked,
  startReading,
}: {
  trip: TripRecord;
  blocked: boolean;
  startReading: OdometerReading | null;
}) {
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const missing = useMissingDocuments(trip.id);
  const endReading = useOdometerReading(trip.id, 'end');
  /** Без километража при връщане заплатата на км не може да се сметне. */
  const needsEnd = endReading === null && trip.endOdometerKm == null;
  const startKm = startReading?.km ?? trip.startOdometerKm ?? null;

  return (
    <div className="space-y-3">
      <Card className="!bg-accent-soft !border-accent">
        <div className="flex items-center gap-3">
          <span className="flex size-12 items-center justify-center rounded-full bg-go text-white">
            <Check size={28} strokeWidth={3} />
          </span>
          <div>
            <div className="text-2xl font-bold">Всички адреси са минати</div>
            <div className="text-lg text-muted">Остава само да приключите курса.</div>
          </div>
        </div>
      </Card>
      {blocked ? (
        <Banner tone="stop">Първо заснемете наново върнатия документ — задачата е най-горе.</Banner>
      ) : null}
      {missing.length > 0 ? (
        <Banner tone="warn">Липсват снимки: {missing.join(', ')}. Заснемете ги, преди да приключите курса.</Banner>
      ) : null}

      <Card>
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'flex size-10 shrink-0 items-center justify-center rounded-full border-2',
              needsEnd ? 'border-brand bg-accent text-accent-ink' : 'border-go bg-go text-white',
            )}
          >
            {needsEnd ? <Gauge size={22} /> : <Check size={22} strokeWidth={3} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-xl font-bold">Километраж при връщане</div>
            <div className="text-base text-muted">
              {endReading
                ? `${endReading.km.toLocaleString('bg-BG')} км — заснет`
                : trip.endOdometerKm != null
                  ? `${trip.endOdometerKm.toLocaleString('bg-BG')} км — въведен от офиса`
                  : 'Снимка на таблото и числото, когато бусът е спрял.'}
            </div>
          </div>
        </div>
        {needsEnd ? (
          <div className="mt-3">
            <BigButton tone="accent" disabled={blocked} icon={<Gauge size={28} />} onClick={() => setCapturing(true)}>
              Снимай километража
            </BigButton>
          </div>
        ) : null}
      </Card>

      <BigButton
        tone="accent"
        disabled={busy || blocked || missing.length > 0 || needsEnd}
        icon={<Check size={28} strokeWidth={3} />}
        onClick={() => {
          setBusy(true);
          void recordEvent({
            tripId: trip.id,
            type: 'completed',
            payload: endReading ? { odometerKm: endReading.km, odometerDocId: endReading.docId } : null,
          })
            .then(() => db.trips.update(trip.id, { status: 'completed' }))
            .finally(() => setBusy(false));
        }}
      >
        Приключи курса
      </BigButton>

      {capturing ? (
        <CaptureFlow
          type="odometer"
          title="Километраж при връщане"
          tripId={trip.id}
          orderStopId={null}
          askOdometer
          odometerMoment="end"
          minOdometerKm={startKm}
          onClose={() => setCapturing(false)}
          onDone={(result) => {
            const photo = result.photos[0];
            if (photo && result.odometerKm !== null) {
              void saveOdometer(trip.id, 'end', { km: result.odometerKm, docId: photo.id });
            }
            setCapturing(false);
          }}
        />
      ) : null}
    </div>
  );
}

/** ─── Върнати документи ───────────────────────────────────────────────── */

/**
 * Офисът е върнал снимка („размазана", „липсва печат") — задачата стои най-горе
 * и спира стъпките, докато не се заснеме наново. Новата снимка замества
 * старата; офисът я преглежда отново.
 */
function ReshootTasks({ tasks }: { tasks: ReshootTask[] }) {
  const [active, setActive] = useState<ReshootTask | null>(null);
  /**
   * Камерата се показва и след като задачата е изчезнала от списъка — първата
   * потвърдена страница вече я затваря, а ЧМР може да има още листове.
   */
  const flow = active ? (
    <CaptureFlow
      type={active.type}
      title={`Наново: ${DOCUMENT_LABELS[active.type]}`}
      tripId={active.tripId}
      orderStopId={active.orderStopId}
      replacesDocumentId={active.documentId}
      onClose={() => setActive(null)}
      onDone={() => setActive(null)}
    />
  ) : null;
  /* Камерата е винаги втори елемент — иначе при изчезването на картата React я пресъздава. */
  return (
    <>
      {tasks.length > 0 ? <ReshootCard tasks={tasks} onShoot={setActive} /> : null}
      {flow}
    </>
  );
}

function ReshootCard({ tasks, onShoot }: { tasks: ReshootTask[]; onShoot: (task: ReshootTask) => void }) {
  return (
    <Card className="!border-stop !bg-stop/10">
      <div className="flex items-center gap-2 text-xl font-bold text-stop">
        <RotateCcw size={24} />
        {tasks.length === 1 ? 'Офисът върна документ' : `Офисът върна ${tasks.length} документа`}
      </div>
      <p className="mt-1 text-lg">Заснемете го наново — дотогава следващите стъпки чакат.</p>
      <ul className="mt-3 space-y-3">
        {tasks.map((task) => (
          <li key={task.documentId} className="rounded-xl border-2 border-line bg-paper p-3">
            <div className="text-xl font-bold">{DOCUMENT_LABELS[task.type]}</div>
            <div className="text-lg text-muted">
              {[task.orderNumber, task.stopCity ?? task.stopName, task.tripNumber ? `курс ${task.tripNumber}` : null]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {task.reason ? (
              <div className="mt-1 text-lg">
                <span className="text-muted">Причина: </span>
                {task.reason}
              </div>
            ) : null}
            <div className="mt-3">
              <BigButton tone="accent" icon={<Camera size={28} />} onClick={() => onShoot(task)}>
                Снимай наново
              </BigButton>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
